import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell,
  ClipboardList,
  CheckCheck,
  ExternalLink,
  Loader2,
  MessageSquare,
  UserPlus,
  Trophy,
  Sparkles,
} from 'lucide-react';
import {
  useUserNotifications,
  type UserNotification,
} from '../../hooks/useUserNotifications';

const ICON_MAP: Record<string, typeof ClipboardList> = {
  ClipboardList,
  Bell,
  MessageSquare,
  UserPlus,
  Trophy,
  Sparkles,
};

function pickIcon(notif: UserNotification) {
  if (notif.icon && ICON_MAP[notif.icon]) return ICON_MAP[notif.icon];
  if (notif.category === 'team_event_signup') return ClipboardList;
  if (notif.category === 'team_contact_inquiry') return MessageSquare;
  if (notif.category === 'platform_contact_inquiry') return MessageSquare;
  if (notif.category === 'tournament_contact_inquiry') return MessageSquare;
  if (notif.category === 'event_page_contact_inquiry') return MessageSquare;
  if (notif.category === 'team_registration_submission') return ClipboardList;
  return Bell;
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  return new Date(iso).toLocaleDateString();
}

interface NotificationBellProps {
  /** Visual variant: dark (header on coloured banner) or light (mobile strip on light bg). */
  variant?: 'dark' | 'light';
}

export function NotificationBell({ variant = 'dark' }: NotificationBellProps) {
  const { notifications, unreadCount, loading, markRead, markAllRead } =
    useUserNotifications({ limit: 50 });
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const updateDropdownPosition = useCallback(() => {
    const trigger = buttonRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewportPadding = 8;
    const width = Math.min(352, window.innerWidth - viewportPadding * 2);
    let left = rect.right - width;
    left = Math.max(
      viewportPadding,
      Math.min(left, window.innerWidth - width - viewportPadding)
    );
    let top = rect.bottom + 8;

    const dropdown = dropdownRef.current;
    if (dropdown) {
      const dropdownHeight = dropdown.offsetHeight;
      const maxBottom = window.innerHeight - viewportPadding;
      if (top + dropdownHeight > maxBottom) {
        const above = rect.top - dropdownHeight - 8;
        if (above >= viewportPadding) top = above;
      }
    }

    setDropdownStyle({
      position: 'fixed',
      top,
      left,
      width,
      zIndex: 9999,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;

    updateDropdownPosition();
    const frame = requestAnimationFrame(updateDropdownPosition);

    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [open, notifications.length, loading, updateDropdownPosition]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const markAllReadRef = useRef(markAllRead);
  markAllReadRef.current = markAllRead;

  useEffect(() => {
    if (open && unreadCount > 0) {
      void markAllReadRef.current();
    }
  }, [open, unreadCount]);

  const recent = notifications.slice(0, 5);

  const buttonColor =
    variant === 'dark'
      ? 'text-white/70 hover:text-white hover:bg-white/10'
      : 'text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800';

  const handleNotificationClick = async (n: UserNotification) => {
    if (!n.read_at) await markRead(n.id);
    setOpen(false);
    if (n.link_url) navigate(n.link_url);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`relative p-2 rounded-lg transition-colors ${buttonColor}`}
        title="Notifications"
        aria-label={
          unreadCount > 0
            ? `Notifications (${unreadCount} unread)`
            : 'Notifications'
        }
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-rose-500 text-white text-[10px] font-bold rounded-full ring-2 ring-[var(--color-primary,#0f766e)] dark:ring-slate-900 leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open &&
        typeof document !== 'undefined' &&
        createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-gray-200 dark:border-slate-700 flex flex-col max-h-[calc(100dvh-5rem)] overflow-hidden"
          role="menu"
        >
          <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700 flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                Notifications
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                {unreadCount > 0
                  ? `${unreadCount} unread`
                  : 'You\u2019re all caught up'}
              </p>
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  void markAllRead();
                }}
                className="inline-flex items-center gap-1 text-xs font-medium text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
                title="Mark all as read"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Mark all read
              </button>
            )}
          </div>

          <div className="overflow-y-auto overflow-x-hidden flex-1 min-h-0">
            {loading && recent.length === 0 ? (
              <div className="flex items-center justify-center py-10 text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : recent.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="w-10 h-10 text-gray-300 dark:text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  No notifications yet
                </p>
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                  When athletes submit forms or sign up for events, you&rsquo;ll
                  see them here.
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-slate-800">
                {recent.map((n) => {
                  const Icon = pickIcon(n);
                  const unread = !n.read_at;
                  return (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => {
                          void handleNotificationClick(n);
                        }}
                        className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-gray-50 dark:hover:bg-slate-800/60 transition-colors ${
                          unread ? 'bg-teal-50/50 dark:bg-teal-900/10' : ''
                        }`}
                      >
                        <div
                          className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                            unread
                              ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                              : 'bg-gray-100 text-gray-500 dark:bg-slate-800 dark:text-slate-400'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p
                            className={`text-sm ${
                              unread
                                ? 'font-semibold text-gray-900 dark:text-white'
                                : 'text-gray-700 dark:text-slate-200'
                            } leading-snug`}
                          >
                            {n.title}
                          </p>
                          {n.body && (
                            <p className="text-xs text-gray-600 dark:text-slate-400 mt-0.5 line-clamp-2">
                              {n.body}
                            </p>
                          )}
                          <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1 flex items-center gap-1">
                            {formatRelative(n.created_at)}
                            {n.link_url && (
                              <span className="inline-flex items-center gap-0.5 text-teal-600 dark:text-teal-400">
                                <ExternalLink className="w-3 h-3" />
                              </span>
                            )}
                          </p>
                        </div>
                        {unread && (
                          <span
                            className="shrink-0 w-2 h-2 rounded-full bg-teal-500 mt-2"
                            aria-hidden
                          />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="border-t border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/80">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="block w-full text-center px-4 py-3 text-sm font-medium text-teal-600 dark:text-teal-400 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
            >
              View all notifications
            </Link>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
